import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MenuController, Platform } from '@ionic/angular/standalone';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Subject, takeUntil } from 'rxjs';
import { Auth0Service } from '../auth0-service/auth0.service';
import { AuthenticationService } from 'src/app/services/authentication-service/authentication.service';
import { take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class IdleService {
  idle = inject(Idle);
  menuCtrl = inject(MenuController);
  private platform = inject(Platform);
  private auth0Service = inject(Auth0Service);
  private router = inject(Router);
  private authenticationService = inject(AuthenticationService);

  private expirationDate: Date | null = null;
  private alreadySubscribe = false;
  // 15 * 60 = 15 minutes
  private TIMEOUT_SECONDS = 15 * 60;

  private ngUnsubscribe: Subject<void> = new Subject();

  constructor() {}

  private subscribe() {
    this.idle.onIdleEnd.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.expirationDate = null;
    });

    this.idle.onIdleStart.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      const seconds = this.idle.getTimeout() * 1000;
      this.expirationDate = new Date(new Date().getTime() + seconds);
    });

    this.idle.onTimeout.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.expirationDate = null;
      this.menuCtrl.close();

      this.logout();
    });

    this.idle.onTimeoutWarning.pipe(takeUntil(this.ngUnsubscribe)).subscribe((countdown) => {
      if (this.expirationDate && new Date() >= this.expirationDate) {
        this.menuCtrl.close();
        this.logout();
      }
    });

    this.platform.resume.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.resumeAction();
    });
  }

  private resumeAction() {
    if (this.expirationDate && new Date() >= this.expirationDate) {
      this.menuCtrl.close();
      this.logout();
    } else {
      if (!this.idle.isRunning()) {
        this.watch();
      }
    }
  }

  watch() {
    if (!this.alreadySubscribe) {
      this.subscribe();
      this.alreadySubscribe = true;
    }

    this.idle.setIdle(5);
    // 900 seconds = 15 min, idle time in seconds
    this.idle.setTimeout(this.TIMEOUT_SECONDS);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this.idle.stop();
    this.idle.watch();
  }

  private logout(): void {
    this.authenticationService
      .isAccessTokenValid()
      .pipe(take(1))
      .subscribe((loggedIn) => {
        if (loggedIn) {
          this.auth0Service.logout();
        }

        this.router.navigate(['/login']);
        this.menuCtrl.close();
        this.watch();
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
